import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { X, Zap, Euro, Ruler, AlertCircle, Upload, Loader2 } from 'lucide-react'
import { FlashCropValidator } from '@/components/admin/FlashCropValidator'
import { uploadFlashFromDataUrl } from '@/lib/flashStorage'
import type { ArtistOnboarding, OnboardingFlash } from '@/types/models'
import type { FlashCropAnalysis } from '@/lib/flashCropValidation'

interface ArtistOnboardingStep3Props {
  onboarding: ArtistOnboarding
  onDataChange: (data: Partial<ArtistOnboarding>) => void
}

export function ArtistOnboardingStep3({ onboarding, onDataChange }: ArtistOnboardingStep3Props) {
  const [flashes, setFlashes] = useState<OnboardingFlash[]>(
    onboarding.flashes || []
  )
  const [editingFlash, setEditingFlash] = useState<string | null>(null)
  const [showUploadFirst, setShowUploadFirst] = useState(true)
  const [uploadingFlashes, setUploadingFlashes] = useState<Set<string>>(new Set())

  // Sync local state with onboarding prop when it changes
  useEffect(() => {
    if (onboarding.flashes) {
      setFlashes(onboarding.flashes)
    }
  }, [onboarding.flashes])

  // Initialize showUploadFirst based on existing flashes
  useEffect(() => {
    if (onboarding.flashes && onboarding.flashes.length > 0) {
      setShowUploadFirst(false)
    }
  }, [onboarding.flashes])

  // Update parent component when flashes change
  useEffect(() => {
    console.log('[Step3] Flashes changed, updating parent:', {
      flashCount: flashes.length,
      flashes: flashes.map(f => ({
        id: f.id,
        imageUrl: f.imageUrl?.substring(0, 50) + '...',
        minPrice: f.minPrice,
        minSize: f.minSize,
        cropValidation: f.cropValidation
      }))
    })
    onDataChange({ flashes })
  }, [flashes]) // Remove onDataChange from dependencies to prevent infinite loop

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    
    files.forEach(file => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const result = e.target?.result as string
        const flashId = `flash_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        
        const newFlash: OnboardingFlash = {
          id: flashId,
          imageUrl: result, // Temporary data URL
          minPrice: '',
          minSize: '',
          filename: file.name,
          cropValidation: {
            isValid: false,
            confidence: 0,
            analyzed: false
            // No error field - only add it when needed
          }
        }
        
        // Add flash to state immediately with data URL
        setFlashes(prev => [...prev, newFlash])
        setEditingFlash(flashId)
        setShowUploadFirst(false)
        
        // Start uploading to Firebase Storage
        setUploadingFlashes(prev => new Set([...prev, flashId]))
        
        try {
          // Upload to Firebase Storage
          const firebaseUrl = await uploadFlashFromDataUrl(
            result,
            onboarding.displayName,
            flashId
          )
          
          // Update flash with Firebase URL
          setFlashes(prev => prev.map(flash => 
            flash.id === flashId 
              ? { ...flash, imageUrl: firebaseUrl }
              : flash
          ))
        } catch (error) {
          console.error('Failed to upload flash image:', error)
          // TODO: Show error toast
        } finally {
          setUploadingFlashes(prev => {
            const next = new Set(prev)
            next.delete(flashId)
            return next
          })
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const updateFlash = (flashId: string, updates: Partial<OnboardingFlash>) => {
    console.log('[Step3] Updating flash:', { flashId, updates })
    setFlashes(prev => {
      const updated = prev.map(flash => 
        flash.id === flashId ? { ...flash, ...updates } : flash
      )
      console.log('[Step3] Flash updated, new flashes:', updated.length)
      return updated
    })
  }

  const removeFlash = (flashId: string) => {
    setFlashes(prev => prev.filter(flash => flash.id !== flashId))
    if (editingFlash === flashId) {
      setEditingFlash(null)
    }
    // Show upload first message if no flashes left
    if (flashes.length === 1) {
      setShowUploadFirst(true)
    }
  }

  const handleCropValidation = (flashId: string, isValid: boolean, analysis?: FlashCropAnalysis, croppedImageUrl?: string) => {
    const updates: Partial<OnboardingFlash> = {
      cropValidation: {
        isValid,
        confidence: analysis?.confidence || 0,
        analyzed: true,
        ...(analysis ? {} : { error: 'Analysis failed' })
      }
    }
    
    // If the image was cropped, update the imageUrl
    if (croppedImageUrl) {
      updates.imageUrl = croppedImageUrl
    }
    
    updateFlash(flashId, updates)
  }

  const isFlashComplete = (flash: OnboardingFlash) => {
    const hasPricing = flash.minPrice.trim() !== '' && flash.minSize.trim() !== ''
    const hasCropValidation = flash.cropValidation?.analyzed && flash.cropValidation?.isValid
    return hasPricing && hasCropValidation
  }

  const completedFlashes = flashes.filter(isFlashComplete)
  const allFlashesValid = flashes.length > 0 && flashes.every(flash => 
    flash.cropValidation?.analyzed && flash.cropValidation?.isValid
  )

  // Example flash images for reference
  const exampleFlashes = [
    {
      url: "https://storage.googleapis.com/getmoretats-by-bareminimum.firebasestorage.app/flashes/VI4HnUKNaUTA88trHaKa/15_240_linework_cartoon.jpg",
      description: "Perfect! Clean background, perfect crop",
      type: "perfect",
      badge: "Preferred"
    },
    {
      url: "https://firebasestorage.googleapis.com/v0/b/getmoretats-by-bareminimum.firebasestorage.app/o/flashes%2F8THpsVdfz63cDa6idfSG%2Fx_450_abstract_flow-lines.jpg?alt=media&token=8afd2051-273b-4dee-83e9-1c69de4a461d",
      description: "Acceptable - Placement photo on skin/avatar is okay if needed",
      type: "acceptable",
      badge: "Okay"
    },
    {
      url: "https://storage.googleapis.com/getmoretats-by-bareminimum.firebasestorage.app/flashes/x08Ju3GddOjbYmR9rZfH/x_90_linework_botanical_woodcut.png",
      description: "Tolerable - Custom background, but minimal (suboptimal but acceptable)",
      type: "tolerable",
      badge: "Acceptable"
    }
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
              4
            </div>
            Flash Tattoos
          </CardTitle>
          <CardDescription>
            Upload your flash designs with pricing. Each image should show only one flash design.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Statistics */}
          <div className="flex items-center gap-4 text-sm">
            <Badge variant="outline" className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {flashes.length} flashes uploaded
            </Badge>
            <Badge variant={completedFlashes.length === flashes.length ? "default" : "secondary"}>
              {completedFlashes.length} completed
            </Badge>
          </div>

          {/* Guidelines Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Flash Guidelines</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>One flash per image</strong> - each upload should show a single design</li>
                    <li>• <strong>Clean backgrounds</strong> - white or very minimal backgrounds work best</li>
                    <li>• <strong>Padding</strong> - zoom out rather than too close, we crop in automatically. Minimum is 12% padding</li>
                    <li>• <strong>No Watermarks</strong> - Flashes are always shown next to your username</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Example Flashes */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Flash Quality Examples</h3>
              <p className="text-sm text-muted-foreground">
                Here are examples showing different quality levels - aim for the first one!
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {exampleFlashes.map((example, index) => (
                <div key={index} className="space-y-3">
                  <div className="relative">
                    <div className={`rounded-lg overflow-hidden border-2 ${
                      example.type === 'perfect' ? 'border-green-500' :
                      example.type === 'acceptable' ? 'border-yellow-500' :
                      'border-orange-500'
                    }`}>
                      <img
                        src={example.url}
                        alt={`Example flash ${index + 1}`}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                    <div className="absolute top-2 right-2">
                      <Badge 
                        variant="secondary"
                        className={`text-xs font-semibold ${
                          example.type === 'perfect' ? 'bg-green-500 text-white' :
                          example.type === 'acceptable' ? 'bg-yellow-500 text-white' :
                          'bg-orange-500 text-white'
                        }`}
                      >
                        {example.badge}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className={`text-xs font-medium ${
                      example.type === 'perfect' ? 'text-green-700' :
                      example.type === 'acceptable' ? 'text-yellow-700' :
                      'text-orange-700'
                    }`}>
                      {example.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upload First Instruction */}
          {showUploadFirst && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Upload className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Start with One Flash</h4>
                    <p className="text-sm text-blue-800 mb-3">
                      Upload one flash first to check if the crop quality is good. We'll analyze the padding 
                      around your design to ensure it meets our standards.
                    </p>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Upload your best flash design first</li>
                      <li>• We'll check the crop automatically</li>
                      <li>• Green overlay = good crop, red overlay = needs adjustment</li>
                      <li>• Once you understand the requirements, upload more!</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Flash Grid with Upload Area */}
          <div className="space-y-4">
            {flashes.length > 0 && <h3 className="font-semibold">Your Flash Designs</h3>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Upload Area Card */}
              <Card className="border-2 border-dashed border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10 transition-colors">
                <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
                  <input
                    type="file"
                    accept="image/*"
                    multiple={!showUploadFirst}
                    onChange={handleImageUpload}
                    className="hidden"
                    id="flash-upload"
                  />
                  <label
                    htmlFor="flash-upload"
                    className="cursor-pointer flex flex-col items-center gap-3 text-center"
                  >
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <Zap className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg mb-1">
                        {showUploadFirst ? 'Upload Your First Flash' : 'Upload More Flashes'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {showUploadFirst 
                          ? 'Start with one image'
                          : 'Select multiple images'
                        }
                      </p>
                    </div>
                  </label>
                </CardContent>
              </Card>

              {flashes.map((flash) => (
                  <Card 
                    key={flash.id} 
                    className={`relative transition-all ${
                      editingFlash === flash.id ? 'ring-2 ring-primary' : ''
                    } ${
                      isFlashComplete(flash) ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'
                    }`}
                  >
                    {/* Upload indicator */}
                    {uploadingFlashes.has(flash.id) && (
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-10">
                        <div className="bg-white rounded-lg p-3 flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      </div>
                    )}

                    <CardContent className="p-4 space-y-3">

                      {/* Image with Crop Validation */}
                      {!uploadingFlashes.has(flash.id) ? (
                        <FlashCropValidator
                          imageUrl={flash.imageUrl}
                          onValidationComplete={(isValid, analysis, croppedImageUrl) => 
                            handleCropValidation(flash.id, isValid, analysis, croppedImageUrl)
                          }
                          className="w-full"
                          autoAnalyze={true}
                          onRemove={() => removeFlash(flash.id)}
                          displayName={onboarding.displayName}
                          flashId={flash.id}
                        />
                      ) : (
                        <div className="w-full aspect-square rounded-lg overflow-hidden border">
                          <img
                            src={flash.imageUrl}
                            alt="Flash design (uploading)"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}

                      {/* Filename */}
                      {flash.filename && (
                        <p className="text-xs text-muted-foreground truncate">
                          {flash.filename}
                        </p>
                      )}

                      {/* Status Badge */}
                      <div className="flex items-center justify-between">
                        <Badge 
                          variant={isFlashComplete(flash) ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {isFlashComplete(flash) ? "Complete" : "Needs pricing"}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFlash(flash.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Pricing Form */}
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Euro className="w-3 h-3" />
                            Minimum Price (in €)
                          </Label>
                          <Input
                            value={flash.minPrice}
                            onChange={(e) => {
                              const numbersOnly = e.target.value.replace(/\D/g, '')
                              const limited = numbersOnly.slice(0, 4)
                              updateFlash(flash.id, { minPrice: limited })
                            }}
                            placeholder="150"
                            className="text-sm"
                            inputMode="numeric"
                            maxLength={4}
                            onFocus={() => setEditingFlash(flash.id)}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Ruler className="w-3 h-3" />
                            Minimum Size (in cm)
                          </Label>
                          <Input
                            value={flash.minSize}
                            onChange={(e) => {
                              const numbersOnly = e.target.value.replace(/\D/g, '')
                              const limited = numbersOnly.slice(0, 3)
                              updateFlash(flash.id, { minSize: limited })
                            }}
                            placeholder="5"
                            className="text-sm"
                            inputMode="numeric"
                            maxLength={3}
                            onFocus={() => setEditingFlash(flash.id)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>

          {/* Summary */}
          {flashes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Flash Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary">{flashes.length}</div>
                    <div className="text-xs text-muted-foreground">Total Flashes</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {flashes.filter(f => f.cropValidation?.analyzed && f.cropValidation?.isValid).length}
                    </div>
                    <div className="text-xs text-muted-foreground">Valid Crops</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{completedFlashes.length}</div>
                    <div className="text-xs text-muted-foreground">Complete</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">{flashes.length - completedFlashes.length}</div>
                    <div className="text-xs text-muted-foreground">Incomplete</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {completedFlashes.length > 0 
                        ? Math.round(completedFlashes.reduce((sum, f) => sum + (parseInt(f.minPrice) || 0), 0) / completedFlashes.length)
                        : 0}€
                    </div>
                    <div className="text-xs text-muted-foreground">Avg. Price</div>
                  </div>
                </div>
                
                {(flashes.length > completedFlashes.length || !allFlashesValid) && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-md space-y-1">
                    {flashes.length > completedFlashes.length && (
                      <p className="text-sm text-orange-800">
                        ⚠️ Complete pricing for all flashes before publishing your profile
                      </p>
                    )}
                    {!allFlashesValid && (
                      <p className="text-sm text-orange-800">
                        ⚠️ All flash crops must be validated and meet quality standards
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
